import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  FormGroup,
  Label,
} from "reactstrap";
import { get, post } from "helpers/api_helper";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import CustomPagination from "components/CustomPagination";
import SummaryCards from "components/SummaryCards";
import { showErrorToast } from "helpers/toastMessages";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { formattedDate } from "helpers/utils/dateUtils";
import { formatToPascal } from "helpers/utils/nameUtils";
import * as api from "../../../constants/api_constants";
import secureLocalStorage from "react-secure-storage";

function Receivables() {
  const navigate = useNavigate();
  const [receivables, setReceivables] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [purposeFilter, setPurposeFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const timerRef = useRef(null);
  const [count, setCount] = useState({
    totalReceivable: 0,
    received: 0,
    partiallyReceived: 0,
    draft: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    sortBy: "receivedAmount",
    direction: "desc",
  });
  const [purposes, setPurposes] = useState([
    { value: "", label: "All Purposes" },
    { value: "Salary", label: "Salary" },
    { value: "Vendor Payment", label: "Vendor Payment" },
    { value: "Reimbursement", label: "Reimbursement" },
    { value: "Bonus", label: "Bonus" },
  ]);

  // Uncomment if API exists to fetch unique purposes
  /*
  const fetchPurposes = useCallback(async () => {
    try {
      const response = await get(api.GET_ALL_PURPOSES);
      const purposeList = response?.data?.map((item) => ({
        value: item,
        label: formatToPascal(item),
      })) || [];
      setPurposes([{ value: "", label: "All Purposes" }, ...purposeList]);
    } catch (error) {
      console.error("Error fetching purposes:", error);
      showErrorToast("Failed to fetch purposes.");
    }
  }, []);
  */

  const fetchReceivablesData = useCallback(
    async (query = "", purpose = "", page = 1, sortBy = "receivedAmount", direction = "desc") => {
      setIsLoading(true);
      setError("");
      const payload = {
        page: page - 1,
        pageSize: 10,
        searchKeyword: query?.trim() ?? "",
        purpose: purpose || "",
        sortBy,
        direction,
      };
      try {
        console.log("Fetching receivables with payload:", payload);
        const response = await post(api.GET_ALL_RECEIVABLES, payload);
        console.log("Receivables API response:", response);

        if (response[0].success) {
          setReceivables(response[0].data.data || []);
          setTotalPages(response[0].data.totalPages || 0);
        } else {
          setError(`Failed to fetch receivables: ${response[0].message || "Unknown error"}`);
          showErrorToast(`Failed to fetch receivables: ${response[0].message || "Unknown error"}`);
        }
      } catch (error) {
        console.error("Error fetching receivables:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          url: api.GET_ALL_RECEIVABLES,
        });
        let errorMessage = error.message || "Please try again";
        if (error.message.includes("Network Error") || error.message.includes("CORS")) {
          errorMessage = "CORS error: Please ensure the server allows requests from this origin or check your network connection.";
        } else if (error.response?.status === 403) {
          errorMessage = "Access denied: Please verify your authentication token or contact support.";
        }
        setError(`Failed to fetch receivables: ${errorMessage}`);
        showErrorToast(`Failed to fetch receivables: ${errorMessage}`);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchCount = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log("Fetching counts from:", api.GET_RECEIVABLES_COUNT);
      const response = await get(api.GET_RECEIVABLES_COUNT);
      console.log("Receivables count response:", response);

      if (response.success) {
        setCount({
          totalReceivable: response.data.totalAmount || 0,
          received: response.data.paidAmount || 0,
          partiallyReceived: response.data.partiallyPaidAmount || 0,
          draft: response.data.draftPaidAmount || 0,
        });
      } else {
        setError(`Failed to fetch receivables count: ${response.message || "Unknown error"}`);
        showErrorToast(`Failed to fetch receivables count: ${response.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error fetching receivables count:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: api.GET_RECEIVABLES_COUNT,
      });
      let errorMessage = error.message || "Please try again";
      if (error.message.includes("Network Error") || error.message.includes("CORS")) {
        errorMessage = "CORS error: Please ensure the server allows requests from this origin or check your network connection.";
      } else if (error.response?.status === 403) {
        errorMessage = "Access denied: Please verify your authentication token or contact support.";
      }
      setError(`Failed to fetch receivables count: ${errorMessage}`);
      showErrorToast(`Failed to fetch receivables count: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSort = useCallback((columnName) => {
    setSortConfig((prevConfig) => {
      const newConfig =
        prevConfig.sortBy === columnName
          ? { sortBy: columnName, direction: prevConfig.direction === "asc" ? "desc" : "asc" }
          : { sortBy: columnName, direction: "asc" };
      setCurrentPage(1);
      fetchReceivablesData(searchQuery, purposeFilter, 1, newConfig.sortBy, newConfig.direction);
      console.log("Sort requested:", newConfig);
      return newConfig;
    });
  }, [fetchReceivablesData, searchQuery, purposeFilter]);

  const SortArrows = ({ column, currentSort }) => {
    const isCurrentColumn = currentSort.sortBy === column;
    return (
      <span className="sort-arrows">
        <i
          className={`fas fa-sort-up ${
            isCurrentColumn && currentSort.direction === "asc" ? "text-primary" : "text-muted"
          }`}
        />
        <i
          className={`fas fa-sort-down ${
            isCurrentColumn && currentSort.direction === "desc" ? "text-primary" : "text-muted"
          }`}
        />
      </span>
    );
  };

  const columnSortMap = {
    "Invoice #": "invoiceNumber",
    From: "field",
    "Primary Contact": "primaryContact",
    Purpose: "purpose",
    "Amount Received": "receivedAmount",
    "Invoice Date": "invoiceDate",
    "Due Amount": "amountDue",
    Status: "receivableStatus",
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        {[
          "Invoice #",
          "From",
          "Primary Contact",
          "Purpose",
          "Amount Received",
          "Invoice Date",
          "Due Amount",
          "Status",
          "Actions",
        ].map((header) =>
          header !== "Actions" ? (
            <th
              key={header}
              onClick={() => handleSort(columnSortMap[header])}
              style={{ cursor: "pointer" }}
            >
              {header}
              <SortArrows column={columnSortMap[header]} currentSort={sortConfig} />
            </th>
          ) : (
            <th key={header}>{header}</th>
          )
        )}
      </tr>
    </thead>
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchReceivablesData(searchQuery, purposeFilter, page, sortConfig.sortBy, sortConfig.direction);
    },
    [fetchReceivablesData, searchQuery, purposeFilter, sortConfig]
  );

  const handleSearch = useCallback(
    (e) => {
      const query = e.target.value ?? "";
      setSearchQuery(query);
      setCurrentPage(1);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        fetchReceivablesData(query?.trim(), purposeFilter, 1, sortConfig.sortBy, sortConfig.direction);
      }, 300);
    },
    [fetchReceivablesData, purposeFilter, sortConfig]
  );

  const handlePurposeFilter = useCallback(
    (e) => {
      const value = e.target.value || "";
      setPurposeFilter(value);
      setCurrentPage(1);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        fetchReceivablesData(searchQuery, value, 1, sortConfig.sortBy, sortConfig.direction);
      }, 300);
    },
    [fetchReceivablesData, searchQuery, sortConfig]
  );

  const getStatusLabelAndColor = (status) => {
    switch (status) {
      case "DRAFT":
        return { label: "Draft", color: "text-info" };
      case "RECEIVED":
        return { label: "Received", color: "text-success" };
      case "PARTIALLY_RECEIVED":
        return { label: "Partially Received", color: "text-muted" };
      default:
        return { label: status || "Unknown", color: "text-black" };
    }
  };

  const handleEdit = useCallback(
    (id) => {
      navigate(`/add-receivables/${id}`);
    },
    [navigate]
  );

  const summaryData = [
    {
      iconClass: "fas fa-users text-primary",
      label: "Total Receivable",
      value: count.totalReceivable || 0,
    },
    {
      iconClass: "fas fa-user-check text-success",
      label: "Received",
      value: count.received || 0,
    },
    {
      iconClass: "fas fa-user-check text-warning",
      label: "Partially Received",
      value: count.partiallyReceived || 0,
    },
    {
      iconClass: "fa-solid fa-circle-half-stroke text-info",
      label: "Draft",
      value: count.draft || 0,
    },
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Uncomment if API exists
        // await fetchPurposes();
        await Promise.all([
          fetchCount(),
          fetchReceivablesData(searchQuery, purposeFilter, currentPage, sortConfig.sortBy, sortConfig.direction),
        ]);
      } catch (err) {
        console.error("Initial fetch failed:", err);
        setError("Failed to load initial data. Please try again.");
        showErrorToast("Failed to load initial data.");
      }
    };
    fetchInitialData();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchCount, fetchReceivablesData, searchQuery, purposeFilter, currentPage, sortConfig]);

  return (
    <div className="page-content">
      <Container fluid={true} className="py-0 px-0">
        <PageSubHeader
          heading={"Receivables"}
          routePath={"Home"}
          pageName={"Receivables"}
        />
        <Row className="px-4">
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          <SummaryCards data={summaryData} />
          <Row>
            <Col>
              <Row className="d-flex justify-content-between align-items-center py-2">
                <Col xs={12} sm={4}>
                  <InputGroup size="sm" className="elbert-search-group">
                    <Input
                      placeholder="Search, Primary Contact"
                      className="elbert-search"
                      onChange={handleSearch}
                      value={searchQuery}
                      disabled={isLoading}
                      aria-label="Search receivables"
                    />
                    <InputGroupText>
                      {isLoading ? (
                        <i className="fas fa-spinner fa-spin" />
                      ) : (
                        <i className="fa fa-search" />
                      )}
                    </InputGroupText>
                  </InputGroup>
                </Col>
                <Col xs={12} sm={3}>
                  <FormGroup>
                    <Label className="fw-bold">Purpose</Label>
                    <Input
                      type="select"
                      value={purposeFilter}
                      onChange={handlePurposeFilter}
                      disabled={isLoading}
                      aria-label="Filter by purpose"
                    >
                      {purposes.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col xs={12} sm={3}></Col>
                <Col
                  xs={12}
                  sm={2}
                  className="d-flex align-items-center justify-content-sm-end justify-content-center mt-2 mt-sm-0"
                >
                  <CustomButton
                    text="+ Add New"
                    onClick={() => navigate("/add-receivables")}
                    disabled={isLoading}
                    aria-label="Add new receivable"
                  />
                </Col>
              </Row>
              <Row>
                <Card>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table mb-0">
                        <colgroup>
                          <col style={{ width: "12%" }} /> {/* Invoice # */}
                          <col style={{ width: "15%" }} /> {/* From */}
                          <col style={{ width: "15%" }} /> {/* Primary Contact */}
                          <col style={{ width: "15%" }} /> {/* Purpose */}
                          <col style={{ width: "12%" }} /> {/* Amount Received */}
                          <col style={{ width: "12%" }} /> {/* Invoice Date */}
                          <col style={{ width: "12%" }} /> {/* Due Amount */}
                          <col style={{ width: "10%" }} /> {/* Status */}
                          <col style={{ width: "9%" }} /> {/* Actions */}
                        </colgroup>
                        {renderTableHeader()}
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan="8" className="text-center">
                                <i className="fas fa-spinner fa-spin" /> Loading receivables...
                              </td>
                            </tr>
                          ) : receivables && receivables.length > 0 ? (
                            receivables.map((row) => {
                              const { label, color } = getStatusLabelAndColor(row.receivableStatus);
                              return (
                                <tr key={row.id}>
                                  <td>{row.invoiceNumber || "N/A"}</td>
                                  <td>{formatToPascal(row.field || "N/A")}</td>
                                  <td>{formatToPascal(row.primaryContact || "N/A")}</td>
                                  <td>{formatToPascal(row.purpose || "N/A")}</td>
                                  <td style={{ textAlign: "right" }}>{parseFloat(row.receivedAmount) || 0}</td>
                                  <td>{formattedDate(row.invoiceDate) || "N/A"}</td>
                                  <td style={{ textAlign: "right" }}>{row.amountDue || 0}</td>
                                  <td className={color}>{label}</td>
                                  <td>
                                    <div className="d-flex gap-3 align-items-center justify-content-center">
                                      <i
                                        className="fa-solid fa-eye action-icon"
                                        title="View"
                                        onClick={() => navigate(`/view-receivables/${row.id}`)}
                                        aria-label={`View receivable ${row.invoiceNumber || "unknown"}`}
                                      ></i>
                                      <i
                                        onClick={() => handleEdit(row.id)}
                                        className="fa-regular fa-pen-to-square action-icon"
                                        title="Edit"
                                        aria-label={`Edit receivable ${row.invoiceNumber || "unknown"}`}
                                      ></i>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center">
                                {searchQuery || purposeFilter
                                  ? `No receivables found for the selected filters.`
                                  : "No receivables found."}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Row>
            </Col>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            disabled={isLoading}
            aria-label="Receivables pagination"
          />
        </Row>
      </Container>
    </div>
  );
}

export default Receivables;